/**
 * Header Node Renderer
 *
 * Renders header-type nodes as large bold editable text.
 * Always renders at full detail (no LOD).
 *
 * Two modes:
 * - Auto mode (default): Fixed font size, box expands to fit text on one line.
 * - AutoSizeText mode (after user resize): Font scales to fill the box, text wraps.
 */

import type { UINode, ScreenMetrics } from '../types';
import type { NodeRenderer, RenderLayers, RenderResult } from '../renderer';
import type { MeasureOptions } from '../content-measure';

/** Fixed font size for auto-expand mode */
export const AUTO_HEADER_FONT_SIZE = 48;

/**
 * Compute font size that fills the header box (manual/resized mode).
 *
 * Iteratively finds the largest font size where the text fits within the
 * available area. Uses a character-based estimation then reduces until
 * the estimated line count fits the available height.
 *
 * Result is clamped to [16px, 120px].
 */
export function computeHeaderFontSize(
	width: number,
	height: number,
	content: string,
): number {
	// Width-proportional cap
	const widthBased = width * 0.14;

	if (!content || content.trim().length === 0) {
		return Math.max(16, Math.min(widthBased, 120));
	}

	// Available text area after padding (12px vertical, 16px horizontal)
	const availW = Math.max(1, width - 32);
	const availH = Math.max(1, height - 24);

	// Bold header text: avg char width ≈ 0.7 * fontSize, line height ≈ 1.1
	const CHAR_WIDTH_RATIO = 0.7;
	const LINE_HEIGHT = 1.1;
	const charCount = content.length;

	// Start with width-proportional estimate, capped at 120
	let fontSize = Math.min(widthBased, 120);

	// Iteratively reduce until estimated text fits the available height
	for (let i = 0; i < 10; i++) {
		const charsPerLine = Math.max(1, Math.floor(availW / (CHAR_WIDTH_RATIO * fontSize)));
		const numLines = Math.ceil(charCount / charsPerLine);
		const totalHeight = numLines * LINE_HEIGHT * fontSize;

		if (totalHeight <= availH) break;
		fontSize *= 0.85; // reduce by 15%
	}

	return Math.max(16, Math.min(fontSize, 120));
}

/**
 * Header renderer for large title text.
 */
export class HeaderRenderer implements NodeRenderer {
	private lastContent: Map<string, string> = new Map();

	render(node: UINode, _metrics: ScreenMetrics, layers: RenderLayers): RenderResult {
		// Skip content replacement while the node is being edited.
		// The user's contentEditable element would be destroyed by setContent (innerHTML).
		const container = layers.html.getContainer(node.id);
		const editingEl = container?.querySelector('.header-text.editing');
		if (editingEl) {
			return { lod: 'full', changed: false };
		}

		const isNewElement = layers.html.isNewlyCreated(node.id);
		const content = this.renderContent(node);
		const lastContentValue = this.lastContent.get(node.id);
		const contentChanged = lastContentValue !== content || isNewElement;

		if (contentChanged) {
			layers.html.setContent(node.id, content);
			this.lastContent.set(node.id, content);
		}

		return { lod: 'full', changed: contentChanged };
	}

	private hasAutoSizeText(node: UINode): boolean {
		const obj = (node.data.obj as Record<string, unknown>) ?? node.data;
		// Explicit flag takes precedence; fall back to legacy manuallyResized
		if (typeof obj.autoSizeText === 'boolean') return obj.autoSizeText;
		return !!obj.manuallyResized;
	}

	private hasWordWrap(node: UINode): boolean {
		const obj = (node.data.obj as Record<string, unknown>) ?? node.data;
		if (typeof obj.wordWrap === 'boolean') return obj.wordWrap;
		// Legacy: manuallyResized headers used pre-wrap wrapping
		if (obj.manuallyResized) return true;
		return false; // header default
	}

	private renderContent(node: UINode): string {
		const obj = (node.data.obj as Record<string, unknown>) ?? node.data;
		const content = this.getContent(obj);
		const displayText = content || 'Header';
		const isEmpty = !content || content.trim().length === 0;
		const autoSizeText = this.hasAutoSizeText(node);
		const wordWrap = this.hasWordWrap(node);

		let fontSize: number;
		let whiteSpace: string;
		let wordBreak: string;

		if (autoSizeText) {
			// AutoSizeText mode: scale font to fill box, allow wrapping
			fontSize = this.computeFontSize(node, content);
			whiteSpace = wordWrap ? 'pre-wrap' : 'nowrap';
			wordBreak = wordWrap ? 'break-word' : 'normal';
		} else {
			// Auto mode: fixed font size, box expands to fit
			fontSize = AUTO_HEADER_FONT_SIZE;
			whiteSpace = wordWrap ? 'pre-wrap' : 'nowrap';
			wordBreak = wordWrap ? 'break-word' : 'normal';
		}

		return `
			<div class="header-node">
				<div class="node-close-btn header-close-btn" data-action="close">&times;</div>
				<div class="header-text${isEmpty ? ' header-empty' : ''}" style="font-size: ${fontSize}px; white-space: ${whiteSpace}; word-break: ${wordBreak}">${this.escapeHtml(displayText)}</div>
			</div>
		`;
	}

	private computeFontSize(node: UINode, content: string): number {
		const w = node.size?.width ?? 400;
		const h = node.size?.height ?? 80;
		return computeHeaderFontSize(w, h, content);
	}

	private getContent(obj: Record<string, unknown>): string {
		return typeof obj?.content === 'string' ? (obj.content as string) : '';
	}

	private escapeHtml(text: string): string {
		return text
			.replace(/&/g, '&amp;')
			.replace(/</g, '&lt;')
			.replace(/>/g, '&gt;')
			.replace(/"/g, '&quot;');
	}

	/**
	 * Content HTML for measurement (auto-fit sizing).
	 *
	 * Always uses auto mode (fixed font size) for deterministic measurement.
	 * AutoSizeText mode font size depends on node.size, which would create a
	 * feedback loop during auto-fit. Uses explicit wordWrap flag only (no
	 * legacy manuallyResized fallback) since measurement is always auto mode.
	 */
	getContentHTML(node: UINode): string {
		const obj = (node.data.obj as Record<string, unknown>) ?? node.data;
		const content = this.getContent(obj);
		const displayText = content || 'Header';
		// For measurement: only honor explicit wordWrap, not legacy manuallyResized
		const wordWrap = typeof obj.wordWrap === 'boolean' ? obj.wordWrap : false;
		const ws = wordWrap ? 'pre-wrap' : 'nowrap';
		const wb = wordWrap ? 'break-word' : 'normal';
		return `<div class="header-text" style="font-size: ${AUTO_HEADER_FONT_SIZE}px; white-space: ${ws}; word-break: ${wb}; overflow: visible">${this.escapeHtml(displayText)}</div>`;
	}

	getMeasureOptions(_node: UINode): MeasureOptions {
		// padding: 1 adds a 2px buffer (1px each side) to the measured width
		// and height. This prevents subpixel truncation: at non-1x zoom the
		// per-keystroke Range measurement and the offscreen content measurer
		// can disagree by ~1px due to font rasterization differences, and
		// .header-text { overflow: hidden } clips the last character.
		return { maxWidth: 2000, minWidth: 100, minHeight: 50, padding: 1 };
	}

	getPreferredSize(node: UINode): { width: number; height: number } {
		return {
			width: node.size?.width ?? 400,
			height: node.size?.height ?? 80
		};
	}

	cleanup(nodeId: string): void {
		this.lastContent.delete(nodeId);
	}
}
